import React, { useState, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import toast from 'react-hot-toast'
import LoadingSpinner from '../common/LoadingSpinner'
import ConfirmationModal from '../common/ConfirmationModal'
import '../../styles/admin-pages.css'

// ── SVG icons ─────────────────────────────────────────────────────────────────
function IconFolder({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  )
}
function IconImage({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  )
}
function IconFile({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
    </svg>
  )
}
function IconVideo({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" />
    </svg>
  )
}
function IconSearch({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}
function IconUpload({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
    </svg>
  )
}
function IconX({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}
function IconEye({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  )
}
function IconTrash({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
    </svg>
  )
}
function IconLink({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatBytes = (bytes) => {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

function fileTypeLabel(mimeType = '') {
  if (mimeType.startsWith('image/')) return 'img'
  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType.startsWith('audio/')) return 'audio'
  if (mimeType.includes('pdf')) return 'pdf'
  return 'file'
}

// ── MediaLibrary ──────────────────────────────────────────────────────────────
const MediaLibrary = () => {
  const [selectedFolder, setSelectedFolder] = useState(null)
  const [folderSearch, setFolderSearch] = useState('')
  const [fileSearch, setFileSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [sortBy, setSortBy] = useState('date')
  const [selectedFiles, setSelectedFiles] = useState([])
  const [detailFile, setDetailFile] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingFileId, setDeletingFileId] = useState(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [isDragActive, setIsDragActive] = useState(false)
  const fileInputRef = useRef(null)
  const queryClient = useQueryClient()

  // ── Queries (preserve all original API calls) ──────────────────────────────
  const { data: foldersData, isLoading: foldersLoading } = useQuery(
    ['media-folders'],
    () => apiService.get('/media/folders').then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  )
  const folders = foldersData?.folders || []

  const { data: orphanedData } = useQuery(
    ['media-orphaned-folders'],
    () => apiService.get('/media/orphaned-folders').then(res => res.data),
    { staleTime: 30000 }
  )
  const orphanedFolders = orphanedData?.folders || []

  const folderId = selectedFolder?.is_admin ? 'admin' : selectedFolder?.id
  const { data: mediaData, isLoading: mediaLoading } = useQuery(
    ['folder-media', folderId, 1, fileSearch, typeFilter],
    () => apiService.get(`/media/folder/${folderId}`, {
      params: { page: 1, limit: 60, search: fileSearch, type: typeFilter }
    }).then(res => res.data),
    { staleTime: 5 * 60 * 1000, enabled: !!folderId }
  )
  const media = mediaData?.media || []

  // ── Mutations ──────────────────────────────────────────────────────────────
  const deleteOrphanedMutation = useMutation(
    (name) => apiService.delete(`/media/orphaned-folder/${encodeURIComponent(name)}`),
    {
      onSuccess: (_, name) => {
        toast.success(`Folder "${name}" deleted`)
        queryClient.invalidateQueries(['media-orphaned-folders'])
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to delete folder')
    }
  )

  const deleteClientFolderMutation = useMutation(
    (clientId) => apiService.delete(`/media/client-folder/${clientId}`),
    {
      onSuccess: (_, clientId) => {
        toast.success('Media folder deleted')
        queryClient.setQueryData(['media-folders'], (old) => {
          if (!old?.folders) return old
          return { ...old, folders: old.folders.filter(f => f.id !== clientId) }
        })
        if (selectedFolder?.id === clientId) setSelectedFolder(null)
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to delete folder')
    }
  )

  const deleteFileMutation = useMutation(
    (id) => apiService.delete(`/media/${id}`),
    {
      onSuccess: () => {
        toast.success('File deleted')
        queryClient.invalidateQueries(['folder-media'])
        queryClient.invalidateQueries(['media-folders'])
        setDetailFile(null)
        setDeletingFileId(null)
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Delete failed')
    }
  )

  const bulkDeleteMutation = useMutation(
    (fileIds) => Promise.all(fileIds.map(id => apiService.delete(`/media/${id}`))),
    {
      onSuccess: () => {
        toast.success(`${selectedFiles.length} file(s) deleted`)
        queryClient.invalidateQueries(['folder-media'])
        queryClient.invalidateQueries(['media-folders'])
        setSelectedFiles([])
        setShowDeleteConfirm(false)
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Bulk delete failed')
    }
  )

  const uploadMutation = useMutation(
    ({ files, clientId }) => {
      const formData = new FormData()
      files.forEach(file => formData.append('files', file))
      if (clientId) formData.append('client_id', clientId)
      return apiService.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    },
    {
      onSuccess: (data) => {
        toast.success(`${data.data?.files?.length || 1} file(s) uploaded`)
        queryClient.invalidateQueries(['media-folders'])
        queryClient.invalidateQueries(['folder-media'])
        setUploadOpen(false)
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Upload failed')
    }
  )

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setIsDragActive(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length) {
      const clientId = selectedFolder?.is_admin ? null : selectedFolder?.id
      uploadMutation.mutate({ files, clientId })
    }
  }, [selectedFolder])

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files)
    if (files.length) {
      const clientId = selectedFolder?.is_admin ? null : selectedFolder?.id
      uploadMutation.mutate({ files, clientId })
    }
    e.target.value = ''
  }

  const handleToggleSelect = (file) => {
    setSelectedFiles(prev => {
      const has = prev.some(f => f.id === file.id)
      return has ? prev.filter(f => f.id !== file.id) : [...prev, file]
    })
  }

  // Filter/sort folders
  const allFolders = [
    ...folders.filter(f => f.company_name.toLowerCase().includes(folderSearch.toLowerCase())),
    ...orphanedFolders.filter(f => f.name.toLowerCase().includes(folderSearch.toLowerCase())).map(f => ({
      id: null, company_name: f.name, media_count: f.count || 0, is_orphaned: true, _name: f.name
    }))
  ]

  // Displayed files
  let displayedMedia = [...media]
  if (sortBy === 'name') displayedMedia.sort((a, b) => (a.original_name || '').localeCompare(b.original_name || ''))
  else if (sortBy === 'size') displayedMedia.sort((a, b) => (b.size || 0) - (a.size || 0))

  // Storage total
  const totalStorageGB = 10
  const usedStorageBytes = folders.reduce((s, f) => s + (f.storage_used || 0), 0)
  const usedStorageGB = usedStorageBytes / (1024 ** 3)
  const storagePercent = Math.min((usedStorageGB / totalStorageGB) * 100, 100)

  return (
    <div style={{ paddingTop: 4 }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Media</div>
          <h1>Media library</h1>
          <div className="subtitle">{folders.reduce((s, f) => s + (f.media_count || 0), 0)} files · {usedStorageGB.toFixed(1)} GB used of {totalStorageGB} GB</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="ds-btn ds-btn-ghost" onClick={() => setUploadOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <IconUpload size={15} /> Upload
          </button>
        </div>
      </div>

      {/* Upload Modal */}
      {uploadOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }} onClick={() => setUploadOpen(false)} />
          <div style={{ position: 'relative', zIndex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, width: 420, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Upload media files</h3>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }} onClick={() => setUploadOpen(false)}><IconX size={16} /></button>
            </div>
            <div
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setIsDragActive(true) }}
              onDragLeave={() => setIsDragActive(false)}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${isDragActive ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 12,
                padding: '32px 16px',
                textAlign: 'center',
                cursor: 'pointer',
                background: isDragActive ? 'rgba(108,99,255,0.05)' : 'var(--bg-0)',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt" onChange={handleFileInputChange} style={{ display: 'none' }} />
              <div style={{ color: 'var(--text-faint)', marginBottom: 10 }}><IconUpload size={36} /></div>
              <p style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', margin: '0 0 4px' }}>{isDragActive ? 'Drop files here!' : 'Drop files or click to browse'}</p>
              <p style={{ fontSize: 11.5, color: 'var(--text-faint)', margin: 0 }}>Images, Videos, Audio, PDFs — max 10 MB each</p>
            </div>
            {uploadMutation.isLoading && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 14, fontSize: 13, color: 'var(--accent)' }}>
                <LoadingSpinner size="sm" /> Uploading…
              </div>
            )}
          </div>
        </div>
      )}

      {/* File delete confirmation */}
      <ConfirmationModal
        isOpen={showDeleteConfirm && selectedFiles.length > 0}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => bulkDeleteMutation.mutate(selectedFiles.map(f => f.id))}
        isLoading={bulkDeleteMutation.isLoading}
        title="Delete Files"
        message={<>Delete <strong>{selectedFiles.length}</strong> file(s)? This cannot be undone.</>}
        confirmText="Delete Files"
      />
      <ConfirmationModal
        isOpen={!!deletingFileId}
        onClose={() => setDeletingFileId(null)}
        onConfirm={() => deleteFileMutation.mutate(deletingFileId)}
        isLoading={deleteFileMutation.isLoading}
        title="Delete File"
        message="Delete this file? This cannot be undone."
        confirmText="Delete File"
      />

      <div className="media-layout">
        {/* ── Sidebar ── */}
        <div className="media-sidebar">
          <div className="media-sidebar-title">Folders</div>

          {/* Folder search */}
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-faint)', pointerEvents: 'none' }}>
              <IconSearch size={13} />
            </span>
            <input
              type="text"
              placeholder="Search…"
              value={folderSearch}
              onChange={e => setFolderSearch(e.target.value)}
              style={{ width: '100%', paddingLeft: 26, paddingRight: 8, paddingTop: 5, paddingBottom: 5, fontSize: 12, borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-0)', color: 'var(--text)', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {foldersLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}><LoadingSpinner size="sm" /></div>
          ) : (
            allFolders.map((f, i) => {
              const isSelected = selectedFolder?.id === f.id && !f.is_orphaned
              const isOrphaned = f.is_orphaned
              return (
                <button
                  key={f.id || f.company_name}
                  className={`media-folder-row${isSelected ? ' selected' : ''}`}
                  onClick={() => {
                    if (isOrphaned) return
                    setSelectedFolder(f)
                    setSelectedFiles([])
                    setFileSearch('')
                  }}
                  style={{ borderLeft: isOrphaned ? '2px solid #ef4444' : 'none', opacity: isOrphaned ? 0.8 : 1 }}
                >
                  <span style={{ color: isSelected ? '#fff' : isOrphaned ? '#ef4444' : 'var(--text-muted)' }}>
                    <IconFolder size={13} color={isSelected ? '#fff' : isOrphaned ? '#ef4444' : undefined} />
                  </span>
                  <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.company_name}
                    {isOrphaned && <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.7 }}>orphaned</span>}
                  </span>
                  <span className="media-folder-count">{f.media_count}</span>
                </button>
              )
            })
          )}

          {/* Storage */}
          <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <div className="media-sidebar-title">Storage</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginBottom: 5 }}>
              <span>{usedStorageGB.toFixed(1)} GB used</span>
              <span>{totalStorageGB} GB total</span>
            </div>
            <div className="ds-progress"><div className="ds-progress-fill" style={{ width: `${storagePercent}%` }} /></div>
          </div>
        </div>

        {/* ── Main area ── */}
        <div className="media-main">
          {/* Filter bar */}
          <div className="filter-bar" style={{ marginBottom: 14 }}>
            <span style={{ color: 'var(--text-faint)' }}><IconSearch size={15} /></span>
            <input
              className="input"
              placeholder={selectedFolder ? `Search in ${selectedFolder.company_name}…` : 'Select a folder first…'}
              value={fileSearch}
              onChange={e => setFileSearch(e.target.value)}
              disabled={!selectedFolder}
              style={{ flex: 1 }}
            />
            <div className="vdiv" style={{ height: 20 }} />
            <div className="segmented">
              {[['', 'All'], ['image', 'Images'], ['video', 'Videos'], ['document', 'Docs']].map(([v, l]) => (
                <button key={v} className={typeFilter === v ? 'on' : ''} onClick={() => setTypeFilter(v)}>{l}</button>
              ))}
            </div>
            <div className="segmented">
              {[['date', 'Date'], ['name', 'Name'], ['size', 'Size']].map(([v, l]) => (
                <button key={v} className={sortBy === v ? 'on' : ''} onClick={() => setSortBy(v)}>{l}</button>
              ))}
            </div>
            {selectedFiles.length > 0 && (
              <button
                className="ds-btn"
                style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 8, padding: '4px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
                onClick={() => setShowDeleteConfirm(true)}
              >
                <IconTrash size={13} /> Delete {selectedFiles.length}
              </button>
            )}
          </div>

          {/* No folder selected */}
          {!selectedFolder ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: 12, color: 'var(--text-faint)' }}>
              <IconFolder size={48} color="var(--border)" />
              <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)', margin: 0 }}>Select a folder</p>
              <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: 0 }}>Pick a folder from the sidebar to browse its files</p>
            </div>
          ) : mediaLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}><LoadingSpinner size="lg" /></div>
          ) : displayedMedia.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: 12 }}>
              <IconImage size={40} />
              <p style={{ fontSize: 13, color: 'var(--text-faint)', margin: 0 }}>
                {fileSearch || typeFilter ? 'No files match your filters' : `No files in ${selectedFolder.company_name} yet — upload some!`}
              </p>
              <button className="ds-btn" onClick={() => setUploadOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <IconUpload size={14} /> Upload files
              </button>
            </div>
          ) : (
            <div className="media-grid">
              {displayedMedia.map((item) => {
                const isImg = item.mime_type?.startsWith('image/')
                const isVid = item.mime_type?.startsWith('video/')
                const typeLabel = fileTypeLabel(item.mime_type)
                const isSelected = selectedFiles.some(f => f.id === item.id)

                return (
                  <div
                    key={item.id}
                    className={`media-tile${isSelected ? ' selected' : ''}`}
                    onClick={() => setDetailFile(item)}
                    style={{ outline: isSelected ? '2px solid var(--accent)' : 'none' }}
                  >
                    <div style={{ position: 'relative', aspectRatio: '1', overflow: 'hidden' }}>
                      {isImg ? (
                        <img
                          src={item.thumbnail_url || item.public_url}
                          alt={item.alt_text || item.original_name}
                          className="media-thumb"
                        />
                      ) : (
                        <div className="media-thumb-placeholder" style={{ background: 'var(--bg-0)' }}>
                          <span style={{ color: 'var(--text-faint)' }}>
                            {isVid ? <IconVideo /> : <IconFile />}
                          </span>
                        </div>
                      )}
                      <span className="media-type-badge">{typeLabel}</span>
                      <input
                        type="checkbox"
                        className="media-tile-check"
                        checked={isSelected}
                        style={{
                          position: 'absolute', top: 7, left: 7, zIndex: 2,
                          opacity: isSelected ? 1 : undefined,
                          accentColor: 'var(--accent)',
                        }}
                        onClick={e => { e.stopPropagation(); handleToggleSelect(item) }}
                        onChange={() => {}}
                      />
                    </div>
                    <div className="media-info">
                      <div className="media-name">{item.original_name}</div>
                      <div className="media-size">{formatBytes(item.size)}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Detail panel ── */}
      {detailFile && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 199, background: 'rgba(0,0,0,0.2)' }} onClick={() => setDetailFile(null)} />
          <div className="media-detail-panel">
            <div className="media-detail-header">
              <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {detailFile.original_name}
              </span>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }} onClick={() => setDetailFile(null)}>
                <IconX size={15} />
              </button>
            </div>
            <div className="media-detail-preview">
              {detailFile.mime_type?.startsWith('image/') ? (
                <img src={detailFile.public_url} alt={detailFile.alt_text || detailFile.original_name} style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, objectFit: 'contain' }} />
              ) : (
                <div style={{ color: 'var(--text-faint)', textAlign: 'center' }}>
                  {detailFile.mime_type?.startsWith('video/') ? <IconVideo size={48} /> : <IconFile size={48} />}
                </div>
              )}
            </div>
            <div className="media-detail-meta">
              {[
                ['Filename', detailFile.original_name],
                ['Type', detailFile.mime_type],
                ['Size', formatBytes(detailFile.size)],
                ['Uploaded', detailFile.created_at ? new Date(detailFile.created_at).toLocaleDateString() : '—'],
              ].map(([k, v]) => (
                <div key={k} className="media-meta-row">
                  <span className="media-meta-label">{k}</span>
                  <span className="media-meta-value" title={v}>{v}</span>
                </div>
              ))}
            </div>
            <div style={{ padding: '12px 16px', display: 'flex', gap: 8, borderTop: '1px solid var(--border)' }}>
              <button
                className="tbl-action-btn"
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
                onClick={() => { navigator.clipboard?.writeText(detailFile.public_url); toast.success('Link copied!') }}
              >
                <IconLink /> Copy link
              </button>
              <button
                className="tbl-action-btn danger"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '4px 12px' }}
                onClick={() => { setDeletingFileId(detailFile.id); setDetailFile(null) }}
              >
                <IconTrash /> Delete
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default MediaLibrary
